import React from "react";

/**
 * Workspace preview — replaces the previous "dashboard mockup".
 * Tighter composite, hairline strokes, three engineering panels:
 *   1) Thermal contour (left)
 *   2) Temperature-time + maturity (middle)
 *   3) Equivalent-age vs strength with calibration markers and now-marker (right)
 * Dark inset to give technical depth on the light page surface.
 */
export const DashboardMockup = () => {
    return (
        <div
            data-testid="dashboard-mockup"
            className="relative border border-[#1a2124] bg-[#0b0f10] shadow-[0_30px_60px_-30px_rgba(1,105,111,0.25)]"
        >
            {/* Title strip */}
            <div className="flex items-center justify-between border-b border-[#1a2124] px-4 py-2.5 bg-[#0e1416]">
                <div className="tc-mono text-[10px] text-[#5a6669] tracking-wider">
                    project · PIER-A03 · pour.04 · mix M-32/PFA-25
                </div>
                <div className="tc-mono text-[10px] text-[#01696f]">
                    ● t = 36 h · curing
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1a2124]">
                {/* ---------- Panel 1: Thermal contour ---------- */}
                <div className="bg-[#0b0f10] p-5">
                    <div className="flex items-center justify-between mb-3">
                        <span className="tc-eyebrow text-[#8a9498]">Thermal · contour</span>
                        <span className="tc-mono text-[10px] text-[#5a6669]">t = 36 h</span>
                    </div>
                    <svg viewBox="0 0 280 220" width="100%" xmlns="http://www.w3.org/2000/svg">
                        <rect x="10" y="10" width="260" height="200" fill="#0e1416" stroke="#1a2124" strokeWidth="0.8" />
                        {[
                            { rx: 110, ry: 80, op: 0.25 },
                            { rx: 85, ry: 60, op: 0.45 },
                            { rx: 58, ry: 42, op: 0.65 },
                            { rx: 32, ry: 22, op: 0.95 },
                        ].map((c, i) => (
                            <ellipse key={i} cx="140" cy="110" rx={c.rx} ry={c.ry} fill="none" stroke="#01696f" strokeWidth="0.9" opacity={c.op} />
                        ))}
                        <circle cx="140" cy="110" r="2" fill="#e6ebec" />
                        <text x="146" y="106" fontSize="8" fill="#e6ebec" fontFamily="JetBrains Mono, monospace">
                            72.4 °C · core
                        </text>
                        <text x="150" y="170" fontSize="7" fill="#8a9498" fontFamily="JetBrains Mono, monospace">
                            48 °C
                        </text>
                        <text x="190" y="60" fontSize="7" fill="#8a9498" fontFamily="JetBrains Mono, monospace">
                            34 °C
                        </text>
                    </svg>
                    <div className="mt-3 tc-mono text-[10px] text-[#5a6669] flex justify-between">
                        <span>ΔT core–surface</span>
                        <span className="text-[#e6ebec]">24.3 °C</span>
                    </div>
                    <div className="mt-1 tc-mono text-[10px] text-[#5a6669] flex justify-between">
                        <span>CIF</span>
                        <span className="text-[#01696f]">0.62 · within limit</span>
                    </div>
                </div>

                {/* ---------- Panel 2: T(t) + maturity ---------- */}
                <div className="bg-[#0b0f10] p-5">
                    <div className="flex items-center justify-between mb-3">
                        <span className="tc-eyebrow text-[#8a9498]">Temperature · maturity</span>
                        <span className="tc-mono text-[10px] text-[#5a6669]">72 h window</span>
                    </div>
                    <svg viewBox="0 0 280 220" width="100%" xmlns="http://www.w3.org/2000/svg">
                        {/* Top sub-panel: T(t) */}
                        <text x="6" y="12" fontSize="8" fill="#8a9498" fontFamily="JetBrains Mono, monospace">
                            T (°C) vs t (h)
                        </text>
                        <line x1="14" y1="100" x2="276" y2="100" stroke="#1a2124" strokeWidth="0.6" />
                        <line x1="14" y1="22" x2="14" y2="100" stroke="#1a2124" strokeWidth="0.6" />
                        <path d="M 14 96 Q 50 50, 90 32 T 180 22 Q 240 22, 276 32" fill="none" stroke="#01696f" strokeWidth="1.3" />
                        <path d="M 14 96 Q 60 84, 100 70 T 200 58 Q 240 56, 276 60" fill="none" stroke="#8a9498" strokeWidth="1" strokeDasharray="3 3" />

                        {/* Bottom sub-panel: maturity (equivalent age) */}
                        <text x="6" y="124" fontSize="8" fill="#8a9498" fontFamily="JetBrains Mono, monospace">
                            t_e (h) · Arrhenius · E_a = 42 kJ/mol
                        </text>
                        <line x1="14" y1="210" x2="276" y2="210" stroke="#1a2124" strokeWidth="0.6" />
                        <line x1="14" y1="132" x2="14" y2="210" stroke="#1a2124" strokeWidth="0.6" />
                        <path d="M 14 208 Q 80 180, 140 158 T 276 138" fill="none" stroke="#01696f" strokeWidth="1.3" />
                    </svg>
                    <div className="mt-2 tc-mono text-[10px] text-[#5a6669] flex justify-between">
                        <span>t_e (now)</span>
                        <span className="text-[#e6ebec]">360 h</span>
                    </div>
                </div>

                {/* ---------- Panel 3: f_c vs t_e ---------- */}
                <div className="bg-[#0b0f10] p-5">
                    <div className="flex items-center justify-between mb-3">
                        <span className="tc-eyebrow text-[#8a9498]">In-place strength</span>
                        <span className="tc-mono text-[10px] text-[#01696f]">monitored</span>
                    </div>
                    <svg viewBox="0 0 280 220" width="100%" xmlns="http://www.w3.org/2000/svg">
                        <text x="6" y="12" fontSize="8" fill="#8a9498" fontFamily="JetBrains Mono, monospace">
                            f_c (MPa) vs t_e (h) · mix calibration
                        </text>
                        <line x1="14" y1="200" x2="276" y2="200" stroke="#1a2124" strokeWidth="0.6" />
                        <line x1="14" y1="30" x2="14" y2="200" stroke="#1a2124" strokeWidth="0.6" />
                        <path d="M 14 198 Q 60 158, 110 122 T 220 70 Q 260 60, 276 56" fill="none" stroke="#01696f" strokeWidth="1.4" />
                        {[
                            { x: 38, y: 184 },
                            { x: 70, y: 142 },
                            { x: 110, y: 116 },
                            { x: 170, y: 84 },
                            { x: 230, y: 62 },
                        ].map((p, i) => (
                            <circle key={i} cx={p.x} cy={p.y} r="2.4" fill="#0b0f10" stroke="#e6ebec" strokeWidth="0.9" />
                        ))}
                        {/* Now marker */}
                        <line x1="138" y1="30" x2="138" y2="200" stroke="#01696f" strokeWidth="0.6" strokeDasharray="2 2" />
                        <circle cx="138" cy="98" r="3" fill="#01696f" />
                        <text x="142" y="94" fontSize="8" fill="#e6ebec" fontFamily="JetBrains Mono, monospace">
                            28.7 MPa
                        </text>
                        {/* Target */}
                        <line x1="14" y1="44" x2="276" y2="44" stroke="#8a9498" strokeWidth="0.5" strokeDasharray="3 3" />
                        <text x="220" y="40" fontSize="7" fill="#8a9498" fontFamily="JetBrains Mono, monospace">
                            f_c'(28) = 40
                        </text>
                    </svg>
                    <div className="mt-2 tc-mono text-[10px] text-[#5a6669] flex justify-between">
                        <span>Formwork release</span>
                        <span className="text-[#01696f]">reached · 24 h</span>
                    </div>
                    <div className="mt-1 tc-mono text-[10px] text-[#5a6669] flex justify-between">
                        <span>PT stressing</span>
                        <span className="text-[#8a9498]">≈ 96 h</span>
                    </div>
                </div>
            </div>

            {/* Footer strip */}
            <div className="border-t border-[#1a2124] bg-[#0e1416] px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <span className="tc-mono text-[10px] text-[#5a6669]">
                    Maturity method · Arrhenius · E_a calibrated for mix M-32/PFA-25
                </span>
                <span className="tc-mono text-[10px] text-[#5a6669]">
                    one engine · predicted & monitored share the same calibration curve
                </span>
            </div>
        </div>
    );
};

export default DashboardMockup;
